import React from 'react';
import { useDispatch } from 'react-redux';
import { WATERMARK_TYPE, serviceSettingsActions } from '../../../reducers/serviceSettings';
import { SelectFileIcon } from '../../SVGIcons';
import watermarkPreviewImage from '../../../../assets/images/watermark_preview.png';
import { getPreviewPositionStyle, calculateWatermarkFontSize } from '../utils';

interface PreviewSectionProps {
  serviceSettings: any;
  selectedFileName: string;
  setSelectedFileName: (name: string) => void;
  onSettingsChanged: () => void;
}

const PreviewSection: React.FC<PreviewSectionProps> = ({
  serviceSettings,
  selectedFileName,
  setSelectedFileName,
  onSettingsChanged,
}) => {
  const dispatch = useDispatch();

  const isWatermarkEnabled = serviceSettings.watermark_type !== WATERMARK_TYPE.NONE;
  const isIndividualInput = serviceSettings.watermark_type === WATERMARK_TYPE.INDIVIDUAL_INPUT;
  const isCorporateLogo = serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO;
  const hasAnnotation = serviceSettings.annotation_is_visible && serviceSettings.annotation_text;

  const shouldShow = isWatermarkEnabled || hasAnnotation;
  if (!shouldShow) return null;

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target?.result as string;
        dispatch(serviceSettingsActions.setCorporateLogoFile({
          name: file.name,
          data: base64Data,
        }));
        dispatch(serviceSettingsActions.setCorporateLogoIsVisible(true));
        onSettingsChanged();
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div>
      {/* ヘッダー行 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
        {isWatermarkEnabled && (
          <div style={{ flex: 1, textAlign: 'center', color: '#444', fontSize: 15, fontWeight: 500 }}>
            {isIndividualInput ? '個別入力' : '企業ロゴ画像'}
          </div>
        )}
        <div style={{ flex: 1, textAlign: 'center', color: '#444', fontSize: 15, fontWeight: 500 }}>
          プレビュー
        </div>
      </div>

      {/* コンテンツ行 */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* 左側: テキスト/ロゴプレビュー */}
        {isIndividualInput && (
          <div style={{ flex: 1 }}>
            <div style={{
              border: '2px solid #bbb',
              borderRadius: 12,
              background: serviceSettings.individual_input_color === '#ffffff' ? '#333333' : '#ffffff',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {serviceSettings.individual_input_text && serviceSettings.individual_input_is_visible ? (
                <div style={{
                  width: 'calc(100% - 16px)',
                  maxWidth: 'calc(100% - 16px)',
                  overflow: 'hidden',
                  color: serviceSettings.individual_input_color,
                  fontSize: `${calculateWatermarkFontSize(serviceSettings.individual_input_text, serviceSettings.individual_input_scale)}px`,
                  fontWeight: 'bold',
                  fontFamily: 'Arial, sans-serif',
                  padding: '0',
                  opacity: (100 - serviceSettings.individual_input_transparency) / 100,
                  textAlign: 'center',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                  whiteSpace: 'nowrap',
                }}>
                  {serviceSettings.individual_input_text}
                </div>
              ) : (
                <div style={{ color: '#888', fontSize: 20 }}>
                  テキストが表示されます
                </div>
              )}
            </div>
          </div>
        )}

        {isCorporateLogo && (
          <div style={{ flex: 1 }}>
            <div style={{
              border: '2px dashed #bbb',
              borderRadius: 12,
              background: '#fafbfc',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {serviceSettings.corporate_logo_file_data ? (
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <img
                    src={serviceSettings.corporate_logo_file_data}
                    alt="logo preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      opacity: (100 - serviceSettings.corporate_logo_transparency) / 100,
                    }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(serviceSettingsActions.setCorporateLogoFile({ name: null, data: null }));
                      setSelectedFileName('');
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '24px',
                      height: '24px',
                      background: 'rgba(220, 53, 69, 0.9)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      fontFamily: 'Arial, sans-serif',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(220, 53, 69, 1)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(220, 53, 69, 0.9)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="削除"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: '32px 0',
                }}>
                  <SelectFileIcon className="w-12 h-12 text-gray-500" />
                  <label style={{ display: 'inline-block', cursor: 'pointer' }}>
                    <input type="file" accept="image/png" style={{ display: 'none' }}
                      onChange={handleLogoChange} />
                    <span style={{
                      background: '#1976d2',
                      color: '#fff',
                      borderRadius: 6,
                      padding: '6px 18px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: 15,
                    }}>
                      画像を選択
                    </span>
                  </label>
                  <div style={{ color: '#888', fontSize: 12, lineHeight: 1.4 }}>
                    透過PNG推奨<br />640 x 360
                  </div>
                </div>
              )}
            </div>
            {serviceSettings.corporate_logo_file_name && (
              <div style={{ color: '#444', fontSize: 12, marginTop: 4, textAlign: 'center' }}>
                {serviceSettings.corporate_logo_file_name}
              </div>
            )}
          </div>
        )}

        {/* 右側: 画像プレビュー */}
        <div style={{ flex: 1 }}>
          <div style={{
            position: 'relative',
            width: '100%',
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #ddd',
            boxSizing: 'border-box',
            background: '#fafbfc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src={watermarkPreviewImage}
              alt="watermark and annotation preview"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            {/* ウォーターマーク: 個別入力 */}
            {isIndividualInput &&
             serviceSettings.individual_input_text &&
             serviceSettings.individual_input_is_visible && (
              <div style={getPreviewPositionStyle(serviceSettings.watermark_position || 'center')}>
                <span style={{
                  maxWidth: 'calc(100% - 16px)',
                  overflow: 'hidden',
                  color: serviceSettings.individual_input_color,
                  fontSize: `${calculateWatermarkFontSize(serviceSettings.individual_input_text, serviceSettings.individual_input_scale)}px`,
                  fontWeight: 'bold',
                  fontFamily: 'Arial, sans-serif',
                  opacity: (100 - serviceSettings.individual_input_transparency) / 100,
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                  whiteSpace: 'nowrap',
                }}>
                  {serviceSettings.individual_input_text}
                </span>
              </div>
            )}
            {/* ウォーターマーク: 企業ロゴ */}
            {isCorporateLogo &&
             serviceSettings.corporate_logo_file_data && (
              <div style={getPreviewPositionStyle(serviceSettings.watermark_position || 'center')}>
                <img
                  src={serviceSettings.corporate_logo_file_data}
                  alt="watermark preview"
                  style={{
                    maxWidth: `${serviceSettings.corporate_logo_scale}%`,
                    maxHeight: `${serviceSettings.corporate_logo_scale}%`,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    opacity: (100 - serviceSettings.corporate_logo_transparency) / 100,
                  }}
                />
              </div>
            )}
            {/* 注釈 */}
            {serviceSettings.annotation_is_visible && serviceSettings.annotation_text && (
              <div style={getPreviewPositionStyle(serviceSettings.annotation_position || 'bottom-center')}>
                <span style={{
                  maxWidth: 'calc(100% - 16px)',
                  overflow: 'hidden',
                  color: serviceSettings.annotation_color || '#ffffff',
                  fontSize: `${calculateWatermarkFontSize(serviceSettings.annotation_text, serviceSettings.annotation_scale ?? 100)}px`,
                  fontWeight: 'bold',
                  fontFamily: 'Arial, sans-serif',
                  opacity: (100 - (serviceSettings.annotation_transparency ?? 0)) / 100,
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                  whiteSpace: 'nowrap',
                }}>
                  {serviceSettings.annotation_text}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewSection;
